// Login — Phase 1 dev-provider form.
//
// The backend /auth/login accepts any valid email and sets the demo session
// cookie (see apps/crm/api/routes/auth.routes.ts). We immediately call
// fetchMe() to hydrate the scope store, then navigate to /dashboard.
//
// TODO [WEEK-5]: swap the email-only form for the real IdP flow (OAuth or
// password) once @govard/auth-adapter switches providers via AUTH_PROVIDER.

import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { Card, Form, Input, Button, Typography, Alert } from 'antd';

import { useAnalytics } from '@govard/analytics';

import { ApiError, apiPost } from '../shared/api-client/index.js';
import { useScopeStore } from '../shared/scope/useCurrentScope.js';

const { Title, Text } = Typography;

interface LoginResponse {
  user: { id: string; email: string };
  defaultWorkspaceId: string;
}

export default function LoginPage(): ReactElement {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const fetchMe = useScopeStore((state) => state.fetchMe);

  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onFinish = async (values: { email: string }): Promise<void> => {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await apiPost<LoginResponse, { email: string }>('/auth/login', { email: values.email });
      await fetchMe();

      const user = useScopeStore.getState().user;
      if (user === null) {
        setErrorMessage('Не удалось сохранить сессию. Повторите попытку.');
        return;
      }

      analytics.track('feature_used', {
        feature: 'auth.login',
        product: 'crm',
      });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorMessage(
          err.status === 400 ? 'Неверный формат email.' : `Ошибка входа (${err.status}).`,
        );
      } else {
        setErrorMessage('Не удалось выполнить вход. Проверьте соединение.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      className="animate-fade-in flex min-h-screen items-center justify-center p-6"
      style={{ backgroundColor: '#fcfaf8' }}
    >
      <Card
        style={{ width: '100%', maxWidth: 400, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}
        bordered={false}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={3} style={{ margin: 0, fontWeight: 700, color: '#44403c' }}>
            Govard CRM
          </Title>
          <Text type="secondary">
            Введите email для входа в демо-режим.
          </Text>
        </div>
        
        <Form
          layout="vertical"
          onFinish={(values) => void onFinish(values)}
          initialValues={{ email: 'demo@govard.tech' }}
          data-testid="login-form"
        >
          <Form.Item
            name="email"
            label={<Text strong style={{ color: '#6b5e4f' }}>Email</Text>}
            rules={[
              { required: true, message: 'Пожалуйста, введите email' },
              { type: 'email', message: 'Неверный формат email' }
            ]}
          >
            <Input
              id="email"
              type="email"
              autoComplete="email"
              disabled={submitting}
              size="large"
            />
          </Form.Item>

          {errorMessage && (
            <Alert
              type="error"
              message={errorMessage}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              data-testid="login-submit"
              size="large"
              block
              loading={submitting}
            >
              Войти
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </main>
  );
}
